import {
  horizontalEllipsis,
  minusKeySymbol,
  plusKeySymbol,
} from '@ipfabric/frontend-shared-assets/utils/characters';
import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type {
  DeviceGroupAttribute,
  HideableNodeType,
  PathLookupEventTopic,
  PathLookupGlobalEventName,
} from '@ipfabric/utils';
import type { EmptyPathLookupResultCause } from 'components/Graph/model/pathLookup';
import { KeyboardKey } from 'components/KeyboardKey/KeyboardKey';
import { TextWithKeyboardKeys } from 'components/KeyboardKey/TextWithKeyboardKeys';
import { tagsInputIpFieldValidation } from 'features/DiscoverySettingsForms/translations/discoverySettingsMessages';
import type { ReactNode } from 'react';
import {
  platformAltKey,
  platformCmdOrCtrlKey,
  platformShiftKey,
} from 'utils/platform/keys';

const loadingStates: { [Key in LoadingState]?: string } & {
  RENDERING: string;
} = {
  INITIAL: 'Initializing',
  LOADING: 'Loading data',
  RENDERING: 'Rendering',
  ERROR: 'Failed to load the graph.',
};

const hideableNodes: { [Key in HideableNodeType]: string } = {
  aciLeaf: 'ACI leaf',
  aciSpine: 'ACI spine',
  ap: 'Access point',
  apic: 'ACI APIC',
  awsAlb: 'AWS ALB',
  awsCne: 'AWS Core Network Edge',
  awsDxgw: 'AWS DX Gateway',
  awsEni: 'AWS Elastic Network Interface',
  awsIgw: 'AWS Internet Gateway',
  awsInstance: 'AWS Instance',
  awsNatGw: 'AWS NAT Gateway',
  awsNlb: 'AWS NLB',
  awsSubnet: 'AWS Subnet',
  awsTgw: 'AWS Transit Gateway',
  awsVgw: 'AWS Virtual Private Gateway',
  awsVpc: 'AWS VPC',
  awsVpcPeering: 'AWS VPC Peering',
  azureAppGw: 'Azure Application Gateway',
  azureErc: 'Azure ExpressRoute Circuit',
  azureFw: 'Azure Firewall',
  azureGw: 'Azure Gateway',
  azureInstance: 'Azure Instance',
  azureLb: 'Azure Load Balancer',
  azureNat: 'Azure NAT Gateway',
  azureNic: 'Azure Network Interface',
  azureSubnet: 'Azure Subnet',
  azureVhub: 'Azure Virtual Hub',
  azureVnet: 'Azure Virtual Network',
  azureVnetPeering: 'Azure VNet Peering',
  cloudInstance: 'Cloud Instance',
  cloudInternetGw: 'Cloud Internet Gateway',
  cloudNatGw: 'Cloud NAT Gateway',
  cloudRouter: 'Cloud Network Router',
  cloudTransitHub: 'Cloud Transit Hub',
  cloudVpnGw: 'Cloud VPN Gateway',
  cloud: 'Cloud',
  cloudLoadBalancer: 'Cloud Load Balancer',
  consoleServer: 'Console server',
  fex: 'Fabric Extender',
  fw: 'Firewall',
  gcpCloudNat: 'GCP Cloud NAT',
  gcpInstance: 'GCP Instance',
  gcpLb: 'GCP Load Balancer',
  gcpNic: 'GCP Network Interface',
  gcpServiceConnect: 'GCP Private Service Connect',
  gcpRouter: 'GCP Router',
  gcpSubnet: 'GCP Subnet',
  gcpVpc: 'GCP VPC',
  gcpVpcPeering: 'GCP VPC Peering',
  host: 'Host',
  l3switch: 'L3 switch',
  lb: 'Load Balancer',
  networkManagement: 'Network management',
  nx7000: 'Nexus 7000',
  phone: 'Phone',
  router: 'Router',
  securityManagement: 'Security management',
  server: 'Server',
  switch: 'Switch',
  transit: 'Transit',
  unknown: 'Unknown',
  vgw: 'VGW',
  waas: 'WAAS',
  wlc: 'WLC',
  // NIM-21654 @see deviceTypes
  // awsGwlb: 'AWS GWLB',
  // awsGwVpce: 'AWS Gateway VPC Endpoint',
  // awsInstance: 'AWS Cloud instance (host)',
  // awsPrivateLink: 'AWS PrivateLink',
  // awsPrivateLinkEndpoint: 'AWS PrivateLink Endpoint',
};

const networkViewerDeviceGroupByOptions: {
  [Key in DeviceGroupAttribute]: string;
} = {
  region: 'Region',
  routingDomain: 'Routing domain',
  siteName: 'Site name',
  stpDomain: 'STP domain',
};

const breadcrumbsDeviceGroupAttributes: {
  [Key in DeviceGroupAttribute]: string;
} = {
  region: 'Regions',
  routingDomain: 'Routing domains',
  siteName: 'Site names',
  stpDomain: 'STP domains',
};

const pathLookupGlobalEventsSummary: {
  [Key in PathLookupGlobalEventName]: string;
} = {
  DEFAULT_GW_NOT_FOUND: 'Default gateway was not found.',
  STARTING_INTERFACE_DOWN: 'One or more starting interfaces are down.',
  LOOP_DETECTED: 'Traffic loop detected',
  MCAST_RECEIVER_NOT_FOUND: 'Multicast receiver was not found.',
  MCAST_SOURCE_NOT_FOUND: 'Multicast source was not found.',
  SIMULATION_ERROR: 'Inconsistent data.',
  SIMULATION_LIMIT_REACHED: 'The calculation was terminated prematurely.',
  CLOUD_LB_POOL_SAMPLED:
    'A load balancer backend pool exceeds the display limit; only a sample of members is shown.',
  CLOUD_SOURCE_IP_NOT_ASSIGNED:
    'The starting IP address is not assigned to anything discovered in its cloud subnet.',
};

const pathLookupEventTopics: { [Key in PathLookupEventTopic]: string } = {
  ACL: 'ACL Policies',
  ZONEFW: 'Zone Firewall Policies',
  FORWARDING: 'Forwarding Issues',
  NAT44: 'NAT (IPv4 to IPv4)',
  PBR: 'PBR',
};

const emptyPathLookupCauses: {
  [Key in EmptyPathLookupResultCause]: { title: string; description: string };
} = {
  'cloud-source-ip-not-assigned': {
    title: 'Starting IP address not assigned in its cloud subnet',
    description:
      'The address belongs to a discovered cloud subnet but no network interface or endpoint in that subnet uses it. Please select a different starting point and try again.',
  },
  'no-dgw': {
    title: 'Default gateway not found',
    description: 'Please select a different starting point and try again.',
  },
  'no-receiver': {
    title: 'Multicast receiver not found',
    description: 'Please select a different destination group and try again.',
  },
  'no-source': {
    title: 'Multicast source not found',
    description: 'Please select a different source and try again.',
  },
  'simulation-error': {
    title: 'Simulation error (e.g. unsupported device) encountered',
    description:
      'We discovered some device we can not properly simulate. Please contact tech support.',
  },
};

export const canvasGraphMessages = {
  en: {
    title: '',
    loadingStates,
    invalidPreset: (name: string): string => `Preset "${name}" is invalid`,
    view: {
      continueToGraphs: 'Continue to graphs',
      errorTitle: 'View error',
      error: {
        notFound: 'Graph view was not found.',
        unknown: 'Failed to load graph view.',
      },
    },
    breadcrumbs: {
      compareMode: {
        label: 'Compare Snapshots',
        vs: 'vs.',
      },
      confirmMainBreadcrumbClick: {
        btnSecondaryConfirm: 'Cancel',
        btnConfirm: 'Start from scratch',
        header: 'Graph will be unloaded',
        subHeader:
          'Are you sure you want to unload the graph and start from scratch?',
      },
      intentCheck: {
        label: 'Intent checks',
      },
      networkViewerMode: {
        label: 'Network viewer',
        groupedBy: (attr: DeviceGroupAttribute): string =>
          `Grouped by ${breadcrumbsDeviceGroupAttributes[attr]}`,
      },
      pathLookupInstance: {
        destination: 'Destination',
        source: 'Source',
        toDefaultGateway: 'to default gateway',
      },
      pathLookupMode: {
        label: 'Path Lookup',
        shortLabel: {
          unicast: 'Unicast Path Lookup',
          multicast: 'Multicast Path Lookup',
          hostToDefaultGW: 'Host to Gateway Path',
        },
      },
    },
    definePathNotification: 'Define the path in the Path Lookup pane',
    emptyGraph: {
      compareSnapshot: {
        title: 'Compare snapshots: Select data to display',

        description: (args: {
          linkNetworkViewer: (content: ReactNode) => ReactNode;
          linkPathChecks: (content: ReactNode) => ReactNode;
          linkPathLookup: (content: ReactNode) => ReactNode;
          linkSavedDiagramViews: (content: ReactNode) => ReactNode;
        }): ReactNode => (
          <>
            <p>
              Please go to either {args.linkNetworkViewer('Network Viewer')} to
              inspect the network topology or{' '}
              {args.linkPathLookup('Path Lookup')} to get a detailed packet
              overview in a selected path.
            </p>
            <p>
              You can also load{' '}
              {args.linkSavedDiagramViews('Saved Diagram Views')} you have
              previously stored or inspect your saved{' '}
              {args.linkPathChecks('Path Checks')}.
            </p>
          </>
        ),
      },
      intentVerification: {
        title: 'Intent checks: Select data to display',

        description: (args: {
          linkNetworkViewer: (content: ReactNode) => ReactNode;
          linkPathChecks: (content: ReactNode) => ReactNode;
          linkPathLookup: (content: ReactNode) => ReactNode;
          linkSavedDiagramViews: (content: ReactNode) => ReactNode;
        }): ReactNode => (
          <>
            <p>
              Please proceed to {args.linkPathChecks('Path Checks')} tab to
              inspect your previously defined Path Checks.
            </p>
            <p>
              You can also go to {args.linkNetworkViewer('Network Viewer')} to
              inspect the network topology, {args.linkPathLookup('Path Lookup')}{' '}
              to get a detailed packet overview in a selected path or load{' '}
              {args.linkSavedDiagramViews('Saved Diagram Views')} you have
              stored previously.
            </p>
          </>
        ),
      },
      defaultEmptyContent: {
        title: 'Diagrams: Select data to display',

        description: (args: {
          linkNetworkViewer: (content: ReactNode) => ReactNode;
          linkPathChecks: (content: ReactNode) => ReactNode;
          linkPathLookup: (content: ReactNode) => ReactNode;
          linkSavedDiagramViews: (content: ReactNode) => ReactNode;
        }): ReactNode => (
          <>
            <p>
              Please proceed to {args.linkPathChecks('Path Checks')} tab to
              inspect your previously defined Path Checks.
            </p>

            <p>
              You can also go to {args.linkNetworkViewer('Network Viewer')} to
              inspect the network topology, {args.linkPathLookup('Path Lookup')}{' '}
              to get a detailed packet overview in a selected path or load{' '}
              {args.linkSavedDiagramViews('Saved Diagram Views')} you have
              stored previously.
            </p>
          </>
        ),
      },
      emptyNetworkViewer: {
        title: "The graph data doesn't contain any devices",

        filterActive: (args: {
          linkDeviceFilter: (content: ReactNode) => ReactNode;
        }): ReactNode => (
          <>
            Some device types are hidden. Try adjusting{' '}
            {args.linkDeviceFilter('device type filters')}.
          </>
        ),

        isolateStpiActive: (args: {
          linkDeactivateStpiIsolate: (content: ReactNode) => ReactNode;
        }): ReactNode => (
          <>
            The graph is configured to display only STP instances, but none are
            present in current graph.{' '}
            {args.linkDeactivateStpiIsolate('Turn off isolating STP instances')}{' '}
            to show all protocols.
          </>
        ),
      },
      emptySharedView: {
        title: "The view can't be loaded",
        description: 'Provided URL is invalid.',
      },
      sharedViewNotLoadedSnapshot: {
        title: "The view can't be loaded",
        description: 'The view uses a snapshot that is not loaded in the db.',
      },
      networkViewer: {
        title: 'Network viewer: Select data to display',
        description:
          'Please define data to visualize. Start by selecting a parameter to group devices by.',
      },
      pathlookup: {
        title: 'Path lookup: Select data to display',
        description: 'Please input data and define a path to visualize.',
      },
      failedPathlookup: {
        title: 'Path lookup could not return any result for given input',
        description: 'This happened due to one or more of following problems:',
        noRelevantFlag:
          'However, we did not find any cause. This seems to be a technical problem of IP Fabric. Please contact tech support.',
        emptyPathLookupCauses,
      },
      tipsOfTheDay: [
        // not meant to be rendered as element array, one is randomly picked

        // biome-ignore lint/correctness/useJsxKeyInIterable: not used as render array
        <TextWithKeyboardKeys>
          Hold <KeyboardKey>{platformCmdOrCtrlKey}</KeyboardKey> or{' '}
          <KeyboardKey>{platformShiftKey}</KeyboardKey> to select nodes in the
          graph.
        </TextWithKeyboardKeys>,

        // biome-ignore lint/correctness/useJsxKeyInIterable: not used as render array
        <TextWithKeyboardKeys>
          Hold <KeyboardKey>{platformAltKey}</KeyboardKey> and use the mouse
          wheel to move UP/DOWN (or all directions if your mouse supports 2-axis
          scroll).
        </TextWithKeyboardKeys>,

        // biome-ignore lint/correctness/useJsxKeyInIterable: not used as render array
        <TextWithKeyboardKeys>
          Hold <KeyboardKey>{platformShiftKey}</KeyboardKey> and use the mouse
          wheel to the LEFT/RIGHT.
        </TextWithKeyboardKeys>,

        // biome-ignore lint/correctness/useJsxKeyInIterable: not used as render array
        <TextWithKeyboardKeys>
          Use <KeyboardKey>{platformCmdOrCtrlKey}</KeyboardKey> +
          <KeyboardKey>{plusKeySymbol}</KeyboardKey> and{' '}
          <KeyboardKey>{minusKeySymbol}</KeyboardKey> to zoom in and out.
        </TextWithKeyboardKeys>,
        'Move mouse over the node to highlight concretes neighborship.',
        "Did you know you could check your network's efficiency by selecting rules in Intent Checks?",
      ],
      tipsOfTheDayWebGl:
        'Enable WebGL to increase graphs rendering performance',
    },
    graphEdgeContextMenu: {
      showDetail: 'Explore',
      showPacketDetail: 'Show packet detail',
    },
    graphNodeContextMenu: {
      collapseNotSelected: 'Collapse non-selected nodes',
      collapseSelected: 'Collapse selected nodes',
      hideNode: 'Hide node',
      hideNotSelected: 'Hide non-selected nodes',
      hideSelected: 'Hide selected nodes',
      layoutAs: `Layout as${horizontalEllipsis}`,
      selectNeighbors: 'Select neighbors',
      selectNeighborsLevel:
        '{number, plural, one {1st} =2 {2nd} =3 {3rd} other {#th}} level of neighbors',
      showDetail: 'Explore',
      uncollapseSelected: 'Uncollapse selected nodes',
      uncollapseSingle: 'Uncollapse',
      unhideNode: 'Unhide node',
      unhideSelected: 'Unhide selected nodes',
    },
    graphSidePanel: {
      layoutSettings: {
        title: 'Visualization Setup',
        backToProtocolsVisualizationSetup:
          'Back to Protocols Visualization Setup',
        btnAddGroup: 'Add a New Group',
        protocolsTab: 'Protocols',
        devicesTab: 'Devices',
        otherProtocols: 'Other Protocols',
        protocol: 'Protocol',
        emptyGroupTitle: <span>Drag and drop protocols&nbsp;here</span>,
        noItems: '(no items)',
        searchPlaceholder: 'Search',
        selectedInstances:
          '({selected} of {count, plural, one {# Instance} other {# Instances}} Selected)',
        leaveDialog: {
          discard: 'Discard',
          save: 'Save',
          saveLeave: 'Save & Leave',
          header: 'Changes in preset are not saved',
          subHeader: 'Do you really want to leave withou save?',
          editPreset: {
            save: 'Save as',
            header: 'Unsaved changes in the preset',
            subHeader:
              'The System preset can\'t be changed but you can save your changes as a new preset - to do so click the button "Save as" and enter the name of the new preset at the bottom of the page.',
          },
        },
        protocolControls: {
          visibility: 'Visibility On/Off',
          grouping: 'Grouping On/Off',
          settings: 'Settings',
          delete: 'Delete',
          editGroupName: 'Edit group name',
        },
        stpiExplanation:
          'Protocol edges aren\'t visible in "Isolate STP instances" mode.',
        presetsControls: {
          btnSaveAs: 'Save as',
          btnSave: 'Save',
          btnReset: 'Reset',
          btnDelete: 'Delete',
          btnCancel: 'Cancel',
          deleteSelectedPreset: 'Delete selected preset?',
          modified: 'Modified',
          deleteConfirm: {
            header: 'Preset will be deleted',
            subHeader: 'Do you really want to delete preset?',
            btnConfirm: 'Delete',
            btnCancel: 'Cancel',
            btnSecondaryConfirm: 'Cancel',
          },
          addPresetPlaceholder: 'Type a preset name',
        },
        presetDraftProcessing: {
          adding: 'Adding preset',
          deleting: 'Deleting preset',
          updating: 'Updating preset',
          resetting: 'Resetting preset',
        },
        protocolSettingsWindow: {
          title: '{name} Settings',
          btnSave: 'Save',
          btnClose: 'Cancel',
          form: {
            centerLabel: 'Center Label',
            linecapLabel: 'Line Cap Label',
            groupName: 'Group Name',
            protocolSettings: (protocolTitle: string): string =>
              `${protocolTitle} Protocol Settings`,
            protocolGroupSettings: 'Protocol Group Settings',
            lineSettings: 'Line settings',
            edgeLabel: 'Label',
            color: 'Line Color',
            pattern: 'Line Style',
            lineWidth: 'Line Width',
            lineWidthTooltip: 'Number of edge represented by a line',
            edgesMax: 'edges max',
            edges: 'Edges',
            upTo: 'Up to',
            moreEdges: 'more edges',
            moreThanXEdges: (x: number): string => `More Than ${x} Edges`,
            none: 'none',
            labelSettings: 'Label settings',
            labels: {
              protocol: 'Protocol',
              subnet: 'Subnet',
              as: 'AS',
              ipAddress: 'IP address',
              prefix: 'Prefix',
              intName: 'Interface name',
              intDescription: 'Interface description',
              area: 'Area',
              cost: 'Cost',
              media: 'Media',
              vlanId: 'Vlan ID',
              wlanId: 'Wlan ID',
              vrf: 'VRF',
            },
            strokeOptions: {
              solid: 'Solid line',
              dashed: 'Dashed line',
              dotted: 'Dotted line',
            },
            validation: {
              required: 'Field is required',
              minValue: 'Value must be greater than {limit}',
              previousValueGreater: 'Value must be lower than following',
              nextValueLower: 'Needs to be lower than the following value',
            },
          },
        },
        stpInstances: {
          findInstances: 'Find Instance(s)',
          instancesCount:
            '({count} {count, plural, one {Instance} other {Instances}})',
          isolateInstances: 'Isolate Instances(s)',
          isolateInstancesTooltip: 'Hide other protocols',
          rootLabel: 'Root (Hostname / ID)',
          vlanLabel: 'VLAN (Name / ID)',
          error: 'Error loading stp instances.',
          filters: {
            rootId: 'Root ID',
            rootHostname: 'Root Hostname',
            vlanId: 'VLAN ID',
            vlanName: 'VLAN Name',
          },
          notFound:
            'No STP instances match selected criteria. Try different input please.',
          noData: 'No STP instances found for selected group(s).',
          notPresetnSelected:
            '{count} selected {count, plural, one {instance is} other {instances are}} missing in selected group.',
          remove: 'Remove',
        },
        hideableNodeTypes: {
          hideNodeType: 'Hide node type',
          showNodeType: 'Show node type',
          labels: hideableNodes,
        },
      },
      networkViewer: {
        title: 'Network Viewer',
        tabs: {
          dataInput: 'Data Input',
          visualizationSetup: 'Visualization setup',
        },
      },
      networkViewerDataInput: {
        btnSubmit: 'Submit',
        groupBy: {
          label: 'Group Devices by Attribute',
          options: networkViewerDeviceGroupByOptions,
          placeholder: 'Select attribute to group devices by',
          required: 'This field is required',
        },
        pathList: {
          filterPlaceholder: 'Search for a specific value',
          label: 'Filter Attribute Values',
          noFilteredPaths: 'No items to show, change or remove search query',
          noGroupBy: 'Select an attribute to group devices by',
          noPaths: 'No items to show, select different attribute to group by',
          numberOfSelected: 'Selected',
          required: 'Please select at least one item',
          error: 'Error fetching group list',
        },
        layoutIconTooltip: 'Change Diagram Layout',
        noValueIconTooltip: "Devices that don't have the selected attribute",
      },
      networkViewerLayout: {
        back: 'Back to Data Input',
        btnCancel: 'Cancel',
        btnSave: 'Save',
      },
      noData: {
        noItemsTitle: 'No items to show,',

        description: (args: {
          linkSelectData: (content: ReactNode) => ReactNode;
        }): ReactNode => (
          <>please {args.linkSelectData('select data')} to display</>
        ),
      },
      compareSnapshots: {
        title: 'Compare Snapshots',
        currentSnapshot: 'Current Snapshot',
        diagramLegend: 'Diagram Legend',
        compareSnapshot: 'Snapshot For Comparison',
        elementsAdded: 'Elements Added',
        elementsRemoved: 'Elements Removed',
        noChanges: 'No Changes',
      },
      pathlookup: {
        title: 'Path Lookup',
        tabs: {
          dataInput: 'Data Input',
          visualizationSetup: 'Visualization Setup',
          resultDetails: 'Result',
          unicast: 'Unicast Path\u00a0Lookup',
          multicast: 'Multicast Tree Lookup',
          hostToDefaultGW: 'Host To\u00a0Gateway',
        },
        visualizationSetup: {
          btnApplySettings: 'Apply Settings',
          tabs: {
            edgeColors: 'Edge colors',
            nodeColors: 'Node colors',
          },
        },
        vrfOptions: {
          autoDetect: 'auto-detect',
          empty: '(empty)',
        },
        dropdownMenu: {
          titleWithDns: 'IP Address / DNS',
          titleWithoutDns: 'IP Address',
        },
        resultDetails: {
          details: 'Details',
          empty: 'No path lookup graph loaded',
          globalEvents: pathLookupGlobalEventsSummary,
          slidePaneClose: 'Back to Events Summary',
        },
        pathLookupEventTopics,
        setupTopics: {
          title: 'Select technology/issues for node coloring',
          titleHelp:
            'When some of the following events occur on a node, color the node by the highest occurred severity.',
        },
        validation: {
          required: 'Required',
          invalidIpv4Address: 'Invalid IPv4 address or range',
          invalidPorts: 'Invalid ports',
          notSameAsStartingPoint: "Can't be same as Starting point",
          notSameAsSource: "Can't be same as Source",
          multicastSource: "Can't use multicast address",
          hostModeOver255:
            'The number of hosts in simulation cannot be over 255.\n Please update the parameters or switch to Network Mode.',
          shortMask: (n: number): string =>
            `Minimum subnet mask length is ${n}`,
          invalidValue: 'Invalid value',
          icmpv4IpTypeMismatch: 'ICMPv4 protocol requires IPv4 addresses',
          icmpv6IpTypeMismatch: 'ICMPv6 protocol requires IPv6 addresses',
        },
        unicast: {
          startingPoint: 'Source IP / CIDR',
          startingPointPlaceholder: 'Starting Point...',
          vrf: 'VRF',
          destinationPoint: 'Destination IP / CIDR',
          destinationPointPlaceholder: 'Destination Point...',
          btnSubmit: 'Submit',
          securedPath: 'Security Rules',
          securedPathTooltip:
            'When a security rule denies traffic, the traffic is dropped here with the “drop” option. With the “continue” option, the path-lookup continues with the traffic regardless.',
          multicastDestinationWarning:
            'You have put in a multicast IP address and you will be redirected the Multicast Path Lookup interface.',
          pathLookupMode: 'Path Lookup Mode',
          networkMode: 'Network Mode',
          networkModeTooltip:
            'Path Look-Up simulation starts and ends with whole networks, individual hosts in source and destination are not considered.',
          hostMode: 'Host Mode',
          hostModeTooltip:
            'simulation starts and ends with each host within the network.',
          ipModeTooltip:
            'Feature is available for range parameters of path lookup only.',
          drop: 'Drop',
          continue: 'Continue',
          firstHopAlgorithm: 'First Hop Algorithm',
          automatic: 'Automatic',
          userDefined: 'User defined',
          swapOnlyIp: 'Swap IP and region',
          swapIpWithPorts: 'Swap IP, region and port',
        },
        multicast: {
          source: 'Source',
          group: 'Group',
          vrf: 'VRF',
          receiver: 'Receiver',
          btnSubmit: 'Submit',
          securedPath: 'Security Rules',
          securedPathTooltip:
            'When a security rule denies traffic, the traffic is dropped here with the “drop” option. With the “continue” option, the path-lookup continues with the traffic regardless.',
          unicastGroupWarning:
            'You have put in a unicast IP address and you will be redirected the Unicast Path Lookup interface.',
          drop: 'Drop',
          continue: 'Continue',
          firstHopAlgorithm: 'First Hop Algorithm',
          automatic: 'Automatic',
          userDefined: 'User defined',
        },
        advancedOptions: {
          advancedPacketOptions: 'Advanced Packet Options',
          application: 'Application',
          applicationRegexMatching: 'Match application as regex',
          destinationPorts: 'Destination Port(s)',
          destinationRegions: 'Destination IP Regions',
          details: {
            moreDetails: 'More details',
            lessDetails: 'Less details',
          },
          discardChanges: 'Discard changes',
          enableRegions: 'IP regions',
          icmpOptions: 'ICMP Options',
          icmpv6Options: 'ICMPv6 Options',
          fragmentOffset: 'Fragment Offset',
          none: 'None',
          protocol: 'Protocol',
          regions: 'IP Regions',
          sourcePorts: 'Source Port(s)',
          sourceRegions: 'Source IP Regions',
          tcpFlags: 'TCP Flags',
          tracked: 'Tracked',
          ttl: 'TTL',
          delete: 'Delete',
          saveAs: 'Save as...',
          save: 'Save',
          deleted: 'Advanced options preset deleted',
          saved: 'Advanced options preset saved',
          deleteConfirm: {
            header: 'Advanced options preset will be deleted',
            subHeader: 'Do you really want to delete preset?',
            btnConfirm: 'Delete',
            btnCancel: 'Cancel',
            btnSecondaryConfirm: 'Cancel',
          },
          anyApplication: 'Any application',
          anyRegion: 'Any region',
        },
        hostToDefaultGW: {
          btnSubmit: 'Submit',
          startingPoint: 'Starting point',
          vrf: 'VRF',
        },
        entryPoints: {
          hostname: 'Hostname',
          iface: 'Interface',
        },
        highlightPacketLoops: {
          title: 'Highlight packet loops',
        },
        edgeColors: {
          title: 'Colors by packet headers priorities',
          titleHelp:
            'Link color priority takes first matching header of specified protocols in their sorted order',
        },
        pathVerification: {
          title: 'Define Path Check',
          tooltip: (
            <span>
              Check whether the path complies with your intended rule.
              <br />
              <br />
              If the path is defined using IP ranges, <strong>“Pass”</strong>{' '}
              option requires ALL packets to reach the defined destinations.{' '}
              <strong>“Fail”</strong> option on the other hand means that NONE
              of the packets reach the defined destinations.
            </span>
          ),
          error: 'Failed to save path check.',
          seeSaved: 'See All Saved Path Status Checks',
          appliedCompareOverlay:
            'Cannot save as path check while snapshot compare is applied',
          appliedIntentCheckOverlay:
            'Cannot save as path check while intent check is applied',
          notSubmittedPathLookup:
            'Please submit the pathlookup form above first',
          form: {
            intendedState: 'Intended Status',
            detectedState: 'Detected Status',
            pass: 'Pass',
            fail: 'Fail',
            btnSave: 'Save Path Check',
            btnSaving: 'Saving',
            btnSaved: 'Saved',
            btnChecking: 'Checking',
          },
          detectedState: {
            pass: 'PATH PASS',
            fail: 'PATH FAIL',
            error: 'ERROR',
          },
        },
      },
      intentVerification: {
        title: 'Intent Checks',
        definedRules: 'Custom Checks',
        builtInRules: 'Built-In Checks',
        pathChecks: 'Path Checks',
        definedRulesSearchPlaceholder: 'Search for a Specific Check',
        definedRulesNoSearchResults:
          'No items matched, change or remove search query',
        noBuiltInChecksForDevices:
          'There are no defined built-in checks for currently visible devices.',
        noCustomChecksForDevices:
          'There are no defined custom checks for currently visible devices.',
      },
      pathChecks: {
        filter: {
          source: 'Source',
          destination: 'Destination',
        },
        item: {
          autoDetect: 'auto-detect',
          source: 'Source',
          destination: 'Destination',
          ports: 'Port(s)',
          firstHopAlgorithm: 'First Hop Algorithm',
          custom: 'сustom',
          vrf: 'VRF',
          protocol: 'Protocol',
          flags: 'Flags',
          empty: '(Empty)',
          indetndedStatus: 'Intended Status',
          detectedState: 'Detected Status',
          receiver: 'Receiver',
          securedPath: 'Apply Security',
          yes: 'Yes',
          no: 'No',
          detectedStateValue: {
            pass: 'pass',
            fail: 'fail',
            error: 'Error!',
            errorShort: 'ERR',
          },
          intendedStateValue: {
            all: 'pass',
            none: 'fail',
          },
        },
        deleteConfirm: {
          header: 'Path check will be deleted',
          subHeader: 'Do you really want to delete path check?',
          btnConfirm: 'Delete',
          btnSecondaryConfirm: 'Cancel',
        },
        pathCheckDeleted: 'Path Check Deleted',
        noSavedPathChecks: 'No Saved Path Checks',
        defineNewPathCheck: 'Define A New Path Check',
        detail: 'Detail',
      },
    },
    graphToolbar: {
      buttons: {
        apiDocumentation: 'API Documentation',
        undo: 'Undo',
        undoDisabled: 'No change to undo',
        redo: 'Redo',
        redoDisabled: 'No change to redo',
        fitView: 'Center View',
        alignDown: 'Align Bottom',
        alignDownDisabled:
          'Select at least 2 nodes to align horizontally to bottom',
        alignLeft: 'Align Left',
        alignLeftDisabled: 'Select at least 2 nodes to align vertically left',
        alignRight: 'Align Right',
        alignRightDisabled: 'Select at least 2 nodes to align vertically right',
        alignUp: 'Align Top',
        alignUpDisabled: 'Select at least 2 nodes to align horizontally to top',
        collapseDirty: 'Uncollapse nodes or collapse more',
        collapseDisabledHidden: 'Cannot collapse hidden nodes',
        collapseDisabledUnselected: 'Select 2 or more nodes to collapse',
        collapseNotSelected: 'Collapse non-selected nodes',
        collapsePristine: 'Collapse selected or non-selected nodes',
        collapseSelected: 'Collapse selected nodes',
        uncollapseSelected: 'Uncollapse selected nodes',
        uncollapseAll: 'Uncollapse all nodes',
        distributeHorizontal: 'Distribute horizontally',
        distributeHorizontalDisabled:
          'Select at least 2 horizontally aligned nodes to distribute horizontally',
        distributeVertical: 'Distribute vertically',
        distributeVerticalDisabled:
          'Select at least 2 vertically aligned nodes to distribute vertically',
        export: 'Export',
        exportToPng: 'Export to PNG',
        exportToSvg: 'Export to SVG',
        exportToVsdx: 'Export to VSDX',
        hideDisabled: 'Select nodes to hide',
        hideNodes: 'Hide selected or non-selected nodes',
        hideNotSelected: 'Hide non-selected nodes',
        hideOrUnhideNodes: 'Hide or unhide nodes',
        hideSelected: 'Hide selected nodes',
        unhideSelected: 'Unhide selected nodes',
        unhideToggle: 'Show hidden nodes',
        unhideAll: 'Unhide all nodes',
        unhideNodes: 'Peek at hidden nodes to unhide them',
        recalculateLayout: 'Recalculate layouts',
        saveView: 'Save View',
        saveViewDisabledNoView: 'Load view to be updated',
        saveViewAs: 'Save View As',
        saveViewAsDisabledCompare: 'Turn off compare mode to save view',
        shareView: 'Share View',
        shareViewDisabledCompare: 'Turn off compare mode to share view',
        searchDiagram: 'Search Diagram',
        selectDataToDisplay: 'Select data to display',
        loadView: 'Load view',
        refreshSnapshot: 'Refresh Snapshot',
        notAvailablePathlookup: 'Not available in path lookup',
        modifySnapshot: 'Modify Snapshot',
      },
      apiDocumentation: {
        title: 'API Documentation',
        requestPayload: 'Request payload',
        copyToClipboard: 'Copy to clipboard',
        copyToClipboardSuccess: 'Coppied to clipboard',
        url: 'URL',
        data: 'data',
        png: 'png',
        svg: 'svg',
        emptyPayload:
          'to see an example of the JSON body, try to load some graph and open this help again',
      },
      dropdownMenu: {
        pathlookup: 'Pathlookup',
      },
      search: {
        placeholder: 'Search in the diagrams…',
        navigate: 'Navigate to the selected element',
      },
    },
    refreshSnapshot: {
      addNewDevicesPlaceholder: 'Insert IP or network',
      addNewDevicesTitle: 'Add new devices',
      btnCancel: 'Cancel',
      btnStart: 'Start',
      selectedDevices:
        'Selected {count, plural, one {# device} other {# devices}}',
      validation: tagsInputIpFieldValidation,
    },
    request: {
      notImplemented: 'Not implemented',
      presetSaved: 'Preset saved',
      presetNotChanged: 'Preset not changed',
      presetDeleted: 'Preset deleted',
      presetReset: 'Preset reset',
      snapshotToCompareNotExists: "Snapshot to compare doesn't exist",
    },
    commands: {
      distribute: {
        noAlignedNodes: {
          x: 'None of selected nodes are aligned horizontally, cannot perform distribution',
          y: 'None of selected nodes are aligned vertically, cannot perform distribution',
        },
      },
      saveUserDefinedLayout: {
        savedSuccessfully: 'User-defined layout has been saved',
      },
      saveView: {
        savedSuccessfully: 'View has been saved successfully',
        nameRequired: 'Please enter a view name',
        typeName: 'Name of the New View',
        cannotSaveView: "The view can't be saved, try it again.",
        confirmDialog: {
          header: 'Current view is already saved',
          subHeader: 'Do you really want to overrite current saved view?',
          btnConfirm: 'Overwrite',
          btnSecondaryConfirm: 'Cancel',
        },
      },
      shareView: {
        btnConfirm: 'OK',
        title: 'Share view url',
      },
      loadView: {
        selectView: 'Select View',
        preview: 'Preview',
        searchViews: 'Search for a specific Saved View',
        noResultsFound: 'No results have been found',
        deleteConfirm: {
          header: 'View will be deleted',
          subHeader: 'Do you really want to delete view?',
          btnConfirm: 'Delete',
          btnCancel: 'Cancel',
          btnSecondaryConfirm: 'Cancel',
        },
      },
      deleteView: {
        title: 'Delete this view?',
        cancel: 'Cancel',
        delete: 'Delete',
      },
    },
    compareSameSnapshotsOverlay: {
      comparingSameSnapshot: "The same snapshots can't be compared.",
      cancel: 'Cancel compare',
    },
    graphPathWarning: {
      hidden: 'This group contains only hidden devices. Click to unhide.',
      empty:
        'This group does not contain any devices. Try adjust device type filters in order to visualize it, please.',
      emptyStpiIsolate:
        'This group does not contain any of the selected STP instances.',
      missing:
        'This group is missing in the current snapshot. Try selecting a different snaphot in order to visualize it, please.',
    },
    graphLayout: {
      fallback:
        'The path contains too many nodes. We used "{fallbackAlgorithm}" as a fallback for performance reasons.',
      universal: {
        title: 'Universal Layout',
        description:
          'Default layout gives reliable results for most of the cases.',
      },
      circular: {
        title: 'Circular Layout',
        description: 'Use this layout for ring topologies.',
      },
      hierarchical: {
        title: 'Hierarchical Layout',
        description: 'Use this layout for layered topologies.',
      },
      upwardTree: {
        title: 'Upward Tree Layout',
        description: 'Use this layout for tree topologies.',
      },
      downwardTree: {
        title: 'Downward Tree Layout',
        description: 'Use this layout for tree topologies.',
      },
      radial: {
        title: 'Radial Layout',
        description:
          'Use this layout for tree topologies to expand outward from center.',
      },
      userDefined: {
        title: 'User-Defined Layout',
        description:
          'Apply previously-stored manually changed positions of nodes.',
      },
    },
    graphLayoutDialog: {
      title: 'Select Diagram Layout Settings',
    },
    changeLayoutTooltip: 'Change Diagram Layout',
    saveOrChangeLayoutTooltip: 'Save User-defined or choose another Layout',
    snapshotCreation: {
      deleteDevicesButton:
        'Delete ({count, plural, one {# row} other {# rows}} selected)',
      refreshDevicesButton:
        'Refresh ({count, plural, one {# row} other {# rows}} selected)',
      startDiscoveryButton:
        'Start discovery ({count, plural, one {# row} other {# rows}} selected)',
      title: 'Modify or create Snapshot',
      description:
        'Create new Snapshot or update existing one by refreshing / deleting devices from selection',
    },
  },
};
